import { useState, useCallback } from 'react';
import { STATUSPAGE } from '@repo/ui';
import axios from 'axios';
import { useService } from 'presentation/app/hooks/useService';

export interface KolbDistributionResponse {
  totalStudentsDistributed: number;
  totalGroupsProcessed: number;
  distributionsByGroup: Array<{
    groupId: string;
    groupName: string;
    studentsAdded: number;
    kolbProfiles: string[];
    strategy: 'ideal' | 'balanced' | 'random';
  }>;
  errors: Array<{
    message: string;
    details?: string;
  }>;
}

export interface CourseData {
  id: string;
  name: string;
  code: string;
  semester: string;
  totalGroups: number;
  groupsWithKolb: number;
  totalStudents: number;
  studentsWithKolb: number;
}

export interface GroupData {
  id: string;
  name: string;
  totalStudents: number;
  studentsWithKolb: number;
  kolbDistribution: {
    AC: number;
    AE: number;
    DO: number;
    RO: number;
    undefined: number;
  };
}

export const useKolbDistribution = () => {
  const { entitiesService } = useService();

  const [courses, setCourses] = useState<CourseData[]>([]);
  const [groups, setGroups] = useState<GroupData[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<CourseData | null>(null);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [distributionResult, setDistributionResult] =
    useState<KolbDistributionResponse | null>(null);
  const [totalStudentsWithKolb, setTotalStudentsWithKolb] = useState(0);

  const [loadingCourses, setLoadingCourses] = useState(STATUSPAGE.INITIAL);
  const [loadingGroups, setLoadingGroups] = useState(STATUSPAGE.INITIAL);
  const [loadingDistribution, setLoadingDistribution] = useState(STATUSPAGE.INITIAL);

  const requestGetCourses = useCallback(
    async (organizationId: string) => {
      setLoadingCourses(STATUSPAGE.LOADING);
      try {
        const response = await axios.get('/admin/courses', {
          params: { organizationId },
        });
        setCourses(response.data.data || []);
        setLoadingCourses(STATUSPAGE.DONE);
      } catch (error) {
        console.error('Erro ao carregar cursos:', error);
        setLoadingCourses(STATUSPAGE.ERROR);
      }
    },
    []
  );

  const requestGetGroupsByCourse = useCallback(
    async (courseId: string | number, organizationId: string) => {
      setLoadingGroups(STATUSPAGE.LOADING);
      try {
        const response = await axios.get('/admin/groups/with-kolb-info', {
          params: {
            organizationId,
            courseId,
          },
        });
        setGroups(response.data.data || []);
        setLoadingGroups(STATUSPAGE.DONE);
      } catch (error) {
        console.error('Erro ao carregar grupos:', error);
        setLoadingGroups(STATUSPAGE.ERROR);
      }
    },
    []
  );

  const requestGetTotalStudentsWithKolb = useCallback(
    async (organizationId: string, courseId: string | number, groupIds?: string[]) => {
      try {
        const body = {
          organizationId,
          courseId,
          ...(groupIds && groupIds.length > 0 && { groupIds }),
        };
        const response = await axios.post('/admin/groups/count-unique-students-with-kolb', body);
        setTotalStudentsWithKolb(response.data.studentsWithKolb || 0);
      } catch (error) {
        console.error('Erro ao contar estudantes com Kolb:', error);
        setTotalStudentsWithKolb(0);
      }
    },
    []
  );

  const requestDistributeStudents = useCallback(
    async (
      organizationId: string,
      courseId: string | number,
      groupIds?: string[]
    ) => {
      setLoadingDistribution(STATUSPAGE.LOADING);
      try {
        const body = {
          organizationId,
          courseId,
          ...(groupIds && groupIds.length > 0 && { groupIds }),
        };

        const response = await axios.post('/admin/groups/distribute-by-kolb', body);

        const data = response.data as KolbDistributionResponse;
        setDistributionResult(data);
        setLoadingDistribution(STATUSPAGE.DONE);
        return data;
      } catch (error) {
        console.error('Error distributing students:', error);
        setLoadingDistribution(STATUSPAGE.ERROR);
        throw error;
      }
    },
    []
  );

  const toggleGroupSelection = useCallback((groupId: string) => {
    setSelectedGroupIds((prev) => {
      if (prev.includes(groupId)) {
        return prev.filter((id) => id !== groupId);
      }
      return [...prev, groupId];
    });
  }, []);

  const toggleAllGroups = useCallback((select: boolean) => {
    if (select) {
      setSelectedGroupIds(groups.map((g) => g.id));
    } else {
      setSelectedGroupIds([]);
    }
  }, [groups]);

  const clearSelection = useCallback(() => {
    setSelectedCourse(null);
    setSelectedGroupIds([]);
    setGroups([]);
    setDistributionResult(null);
  }, []);

  const getTotalStudentsWithKolb = useCallback(() => {
    return totalStudentsWithKolb;
  }, [totalStudentsWithKolb]);

  return {
    courses,
    groups,
    selectedCourse,
    selectedGroupIds,
    distributionResult,
    loadingCourses,
    loadingGroups,
    loadingDistribution,
    requestGetCourses,
    requestGetGroupsByCourse,
    requestDistributeStudents,
    requestGetTotalStudentsWithKolb,
    setSelectedCourse,
    toggleGroupSelection,
    toggleAllGroups,
    clearSelection,
    getTotalStudentsWithKolb,
  };
};
